import React, { useMemo, useState, useSyncExternalStore } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";
import {
  LuFileDown, LuFileText, LuChevronRight, LuInfo, LuLayers, LuRecycle,
  LuClock, LuGauge, LuChartColumn, LuWrench, LuSparkles,
  LuLightbulb, LuBookOpen, LuClipboardList, LuQrCode,
  LuBox, LuRefreshCw, LuLeaf, LuCloud, LuAward, LuScale,
  LuPackage, LuBoxes, LuBuilding2,
} from "react-icons/lu";
import { getOrgTokens } from "../../../theme/style/organizationPalette";
import { M3_BRAND, getM3Colors } from "../../../theme/style/m3Palette";
import { M3Button, M3Surface } from "../../../components/m3";
import MetricCard from "../../../app_components/MetricCard";
import AgGrid from "../../../components/grid/AgGrid";
import { defaultColDef, leftAlignColDef, rightAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import {
  projectSummary, materials, recommendations, benchmarks,
  designFramework, designStore, computeDciFromState, dciGradeInfo,
  energySeed, waterSeed, economicSeed,
  inputMaterialSeed, outputMaterialSeed,
} from "./reportData";
import CircularChart from "../../../circularity-components/CircularChart";
import CircularPCIChart from "../../../circularity-components/CircularPCIChart";
import NomarlizedGwpVsPercentageMciChart from "../../../circularity-components/NomarlizedGwpVsPercentageMciChart";
import NomarlizedGwpVsPercentagePciChart from "../../../circularity-components/NomarlizedGwpVsPercentagePciChart";
import { computePciData } from "../../../circularity-components/computePciData";
import InputMaterialChart from "./InputMaterialChart";
import OutputMaterialChart from "./OutputMaterialChart";
import { exportCircularityAsDocx } from "../../../utils/docxUtils/circularityExportAsDocx";
import {
  calculate_eol_recycling_waste,
  calculate_eol_feedstock_waste,
  calculate_eol_unrecoverable_waste,
  calculate_om_waste_content,
  calculate_om_landfill_waste,
} from "../../../functions/CircularityFunctions";


// Arivu brand green (M3 primary) used for report accents.
const GREEN = M3_BRAND.primary;

// Live-data module-level vars — set synchronously before any child section renders
let liveReportTitle = "Circularity Report";
let liveProductName = null;
let liveProductId = null;
let liveSubCategory = null;
let liveServiceLife = 0;
let liveCombinedData = null;  // { mass_sum, cost_sum, gwp_total_sum, mci_sum, percentage_product_mci, combined_circularities, service_life }
let liveTopic = "Product";

const getReportTitle = () => liveReportTitle;
const getProductName = () => liveProductName || projectSummary.name;
const getProductId = () => liveProductId || projectSummary.code;
const getSubCategory = () => liveSubCategory || "Building envelope";
const getServiceLife = () => liveServiceLife || 60;
const getLiveMaterials = () => liveCombinedData?.combined_circularities?.length ? liveCombinedData.combined_circularities : materials;
const getLiveMassSum = () => liveCombinedData?.mass_sum || projectSummary.totalMass;
const getLiveGwpSum = () => liveCombinedData?.gwp_total_sum || projectSummary.totalGwp;
const getLiveMciPct = () => liveCombinedData?.percentage_product_mci != null ? (liveCombinedData.percentage_product_mci * 100) : projectSummary.projectMci;
const getTopic = () => liveTopic;

// Report labels are authored around "Product" — this word-boundary swap lets
// the same section components serve Project/Building circularity reports
// without forking every label string.
const renameProductWord = (str) => {
  if (typeof str !== "string" || liveTopic === "Product") return str;
  return str
    .replace(/\bProducts\b/g, `${liveTopic}s`)
    .replace(/\bProduct\b/g, liveTopic)
    .replace(/\bproducts\b/g, `${liveTopic.toLowerCase()}s`)
    .replace(/\bproduct\b/g, liveTopic.toLowerCase());
};

const useReportTok = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const m3 = getM3Colors(isDark);
  return { t, theme, isDark, tok, m3 };
};

/** Stand-in for the reference's `PanelCard` (imported from "./Overview" there, not available here) — a bordered card with a title header, matching this app's existing Circularity panel chrome. */
function PanelCard({ title, children }) {
  const { tok } = useReportTok();
  return (
    <M3Surface style={{ alignSelf: "start" }}>
      <Box sx={{ px: tok.spacing.tablePadH, py: tok.spacing.tablePadV, borderBottom: `1px solid ${tok.border.divider}` }}>
        <Typography sx={{ ...tok.tableTitle, fontSize: tok.font.size.sectionTitle, textAlign: "left" }}>{title}</Typography>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </M3Surface>
  );
}

const sectionList = [
  { key: "product", label: "Product Information", icon: LuInfo, blurb: "Identification, category, service life, standards.", source: "Scope & Boundary + Overview", status: "complete" },
  { key: "scope", label: "Scope, Boundary & Assumptions", icon: LuClipboardList, blurb: "User type, boundaries, materiality, indicator scope.", source: "Scope & Boundary tab", status: "complete" },
  { key: "composition", label: "Material Composition", icon: LuLayers, blurb: "Type, mass, GWP, renewable content, data source, confidence.", source: "Overview tab", status: "complete" },
  { key: "flows", label: "Circularity Inputs & Outputs", icon: LuRecycle, blurb: "Combined inflow (Input) and outflow (Output) profile.", source: "Utility → Input + Output", status: "complete" },
  { key: "utilityPerf", label: "Utility Performance", icon: LuClock, blurb: "Utility, energy, water, economic and end-of-life performance.", source: "Utility tab (incl. End of Life)", status: "complete" },
  { key: "designAssessment", label: "Design Assessment (PCI)", icon: LuWrench, blurb: "Connection Type, Connection Accessibility, Independency, and Product Edge Geometry scoring.", source: "Design Assessment tab", status: "complete" },
  { key: "productMci", label: "Material Circularity Indicator Summary", icon: LuGauge, blurb: "Per-material LFI/MCI, worked example, and aggregate MCI with driver commentary.", source: "MCI Calculation tab + Analytics tab", status: "complete" },
  { key: "analytics", label: "Analytics", icon: LuChartColumn, blurb: "GWP vs MCI and input mix, re-explained in prose.", source: "Analytics tab", status: "complete" },
];

// Building-only report sections — appended after the MCI Summary section (mirrors
// the PCI/SCI/BCI Calculation tabs on the Building Circularity page). Placeholder
// only; no calculation logic wired yet.
const buildingCalcSectionList = [
  { key: "pciCalculation", label: "PCI Calculation", icon: LuPackage, blurb: "Product Circularity Indicator calculation for this building.", source: "PCI Calculation tab", status: "pending" },
  { key: "sciCalculation", label: "SCI Calculation", icon: LuBoxes, blurb: "Structural Circularity Indicator calculation for this building.", source: "SCI Calculation tab", status: "pending" },
  { key: "bciCalculation", label: "BCI Calculation", icon: LuBuilding2, blurb: "Building Circularity Indicator calculation for this building.", source: "BCI Calculation tab", status: "pending" },
];

const getSectionListForTopic = (topic) => {
  if (topic !== "Building") return sectionList;
  const insertAt = sectionList.findIndex((s) => s.key === "productMci") + 1;
  return [
    ...sectionList.slice(0, insertAt),
    ...buildingCalcSectionList,
    ...sectionList.slice(insertAt),
  ];
};

const colors = ["#e5766a", "#c58bce", "#78d4b6", "#7ed07e", "#7fb5e6", "#efd67a"];

/* ═══════════════════════════ Main panel ═══════════════════════════ */

const CircularityReportPanel = ({
  scopeEnergy = false, scopeWater = false, scopeEconomic = false,
  reportTitle = "Circularity Report", productName,
  productId = null, productSubCategory = null, serviceLife = 0,
  combined_data = null,
  boundaryState = null,
  topic = "Product",
}) => {
  const { t, tok, m3 } = useReportTok();
  const [selected, setSelected] = useState("product");
  const [fullMode, setFullMode] = useState(false);
  const activeSectionList = useMemo(() => getSectionListForTopic(topic), [topic]);

  // Synchronous render-time overrides
  liveReportTitle = reportTitle;
  liveProductName = productName || null;
  liveProductId = productId || null;
  liveSubCategory = productSubCategory || null;
  liveServiceLife = serviceLife || 0;
  liveCombinedData = combined_data;
  liveTopic = topic || "Product";
  const liveBoundary = boundaryState || {
    start: "2026-01-01",
    end: "2026-12-31",
    cutoff: "Fiscal year-end",
    flow: "gate",
    materiality: 95,
  };

  const handlePrint = () => {
    document.body.classList.add("printing-report");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("printing-report");
    }, 50);
  };

  const handlePptx = async () => {
    const PptxGenJS = (await import("pptxgenjs")).default;
    const pptx = new PptxGenJS();
    pptx.layout = "LAYOUT_WIDE";
    pptx.title = `${getProductName()} — Circularity Report`;

    // Cover
    const cover = pptx.addSlide();
    cover.background = { color: "F0F7F0" };
    cover.addText("CIRCULARITY REPORT", { x: 0.7, y: 1.2, w: 12, h: 0.5, fontSize: 14, color: "2f6b3a", bold: true, fontFace: "Calibri" });
    cover.addText(getProductName(), { x: 0.7, y: 1.9, w: 12, h: 1.2, fontSize: 44, bold: true, color: "1a3d24", fontFace: "Calibri" });
    cover.addText(`Reference: ${projectSummary.code}`, { x: 0.7, y: 3.2, w: 12, h: 0.4, fontSize: 16, color: "555555" });
    cover.addText(`ISO 59020 · ISO 59040 aligned · ${new Date().toLocaleDateString()}`, { x: 0.7, y: 6.8, w: 12, h: 0.4, fontSize: 12, color: "777777" });

    // Executive KPI dashboard
    const kpi = pptx.addSlide();
    kpi.addText("Executive Summary", { x: 0.5, y: 0.3, fontSize: 24, bold: true, color: "1a3d24" });
    const kpis = [
      { label: "Project MCI", value: `${projectSummary.projectMci}%` },
      { label: "Total Mass", value: `${projectSummary.totalMass.toLocaleString()} kg` },
      { label: "Embodied Carbon", value: `${projectSummary.totalGwp.toLocaleString()} t CO₂e` },
      { label: "Materials", value: `${materials.length}` },
    ];
    kpis.forEach((k, i) => {
      const x = 0.5 + i * 3.15;
      kpi.addShape("rect", { x, y: 1.2, w: 2.9, h: 1.6, fill: { color: "E8F0E4" }, line: { color: "7dbf3f", width: 1 } });
      kpi.addText(k.label, { x, y: 1.3, w: 2.9, h: 0.4, fontSize: 11, color: "555555", align: "center" });
      kpi.addText(k.value, { x, y: 1.8, w: 2.9, h: 0.9, fontSize: 28, bold: true, color: "1a3d24", align: "center" });
    });

    // One slide per section
    activeSectionList.forEach((s, i) => {
      const slide = pptx.addSlide();
      slide.addText(`${i + 1}. ${s.label}`, { x: 0.5, y: 0.3, fontSize: 22, bold: true, color: "1a3d24" });
      slide.addText(s.blurb, { x: 0.5, y: 0.9, w: 12, h: 0.4, fontSize: 12, color: "666666", italic: true });
      const bullets = sectionBullets(s.key);
      slide.addText(bullets.map((b) => ({ text: b, options: { bullet: true } })), {
        x: 0.7, y: 1.6, w: 11.5, h: 5, fontSize: 14, color: "222222", paraSpaceAfter: 6,
      });
      slide.addText(`Source: ${s.source} · ISO 59020 / 59040`, { x: 0.5, y: 6.9, w: 12, h: 0.3, fontSize: 10, color: "999999" });
    });

    pptx.writeFile({ fileName: `${getProductName()}-circularity-report.pptx` });
  };

  const handleDocx = async () => {
    const today = new Date().toLocaleDateString("en-CA");
    const mats = getLiveMaterials();
    const totalMass = mats.reduce((s, m) => s + (m.mass || 0), 0);
    const virginMass = mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_virgin_content || 0) / 100), 0);
    const recycledInflow = mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_recycled_content || 0) / 100), 0);
    const recycledOut = mats.reduce((s, m) => s + (m.mass || 0) * ((m.eol_recycling || m.om_recycling || 0) / 100), 0);
    const lossOut = totalMass - recycledOut > 0 ? totalMass - recycledOut : 0;
    const projectMci = getLiveMciPct();
    const gwpSum = getLiveGwpSum();
    const serviceLifeVal = getServiceLife();

    const weightedMci = totalMass > 0
      ? mats.reduce((s, m) => s + (m.row_mci || 0) * (m.mass || 0), 0) / totalMass
      : 0;
    const renewableMass = mats.reduce((s, m) => s + (m.mass || 0) * ((m.renewablePct || 0) / 100), 0);
    const renewablePct = totalMass > 0 ? (renewableMass / totalMass * 100) : 0;

    await exportCircularityAsDocx({
      productName: getProductName(),
      code: getProductId(),
      topic: getTopic(),
      today,
      mats,
      totalMass,
      virginMass,
      recycledInflow,
      recycledOut,
      lossOut,
      projectMci,
      gwpSum,
      boundary: liveBoundary,
      serviceLife: serviceLifeVal,
      weightedMci,
      renewablePct
    });
  };

  return (
    <Box sx={{ display: "grid", gap: 2, textAlign: "left" }}>
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body.printing-report {
            margin: 0;
            padding: 0;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body.printing-report * {
            visibility: hidden !important;
          }
          body.printing-report #full-report,
          body.printing-report #full-report * {
            visibility: visible !important;
          }
          body.printing-report #full-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 210mm !important;
            background: white !important;
            color: #171717 !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .report-page {
            width: 210mm !important;
            height: 297mm !important;
            page-break-after: always !important;
            page-break-inside: avoid !important;
            box-sizing: border-box !important;
            padding: 20mm !important;
            margin: 0 !important;
            position: relative;
            background: white !important;
          }
          .report-page:last-child { page-break-after: auto !important; }
          .avoid-break { page-break-inside: avoid !important; }
        }
        #full-report { display: none; }
        body.printing-report #full-report { display: block; }
      `}</style>

      <M3Surface padded>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, display: "grid", placeItems: "center", borderRadius: tok.radius.icon, backgroundColor: m3.primaryContainer, color: m3.onPrimaryContainer }}>
              <LuFileText size={20} />
            </Box>
            <Box sx={{ textAlign: "left" }}>
              <Typography sx={{ fontWeight: tok.font.weight.semibold, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t("Circularity Report")}</Typography>
              <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.secondary, fontFamily: tok.font.family, textAlign: "left" }}>
                {t("ISO 59040:2025 aligned")} • {t("Consolidates every completed Circularity module")}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <M3Button variant="outlined" icon={<LuBookOpen size={16} />} onClick={() => setFullMode((v) => !v)}>
              {fullMode ? t("Section view") : t("Full Report")}
            </M3Button>
            <M3Button variant="filled" icon={<LuFileDown size={16} />} onClick={handleDocx}>
              {t("Download Word")}
            </M3Button>
          </Box>
        </Box>
      </M3Surface>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "320px 1fr" } }}>
        <PanelCard title={t("Report Contents")}>
          <Box component="ol" sx={{ display: "grid", gap: 0.75, m: 0, p: 0, listStyle: "none" }}>
            {activeSectionList.map((s, i) => {
              const Icon = s.icon;
              const active = s.key === selected;
              return (
                <Box component="li" key={s.key} sx={{ listStyle: "none" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setSelected(s.key)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.25, width: "100%", textAlign: "left",
                      border: `1px solid ${active ? m3.primary : tok.border.card}`,
                      backgroundColor: active ? m3.secondaryContainer : "transparent",
                      borderRadius: tok.radius.input, px: 1.5, py: 1, cursor: "pointer", fontFamily: tok.font.family,
                      "&:hover": { backgroundColor: active ? undefined : m3.stateHover },
                    }}
                  >
                    <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: "11px", fontWeight: 600, flexShrink: 0, backgroundColor: active ? m3.primary : m3.primaryContainer, color: active ? m3.onPrimary : m3.onPrimaryContainer }}>
                      {i + 1}
                    </Box>
                    <Icon size={16} color={GREEN} style={{ flexShrink: 0 }} />
                    <Typography sx={{ flex: 1, fontSize: tok.font.size.bodyText, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t(renameProductWord(s.label))}</Typography>
                    <LuChevronRight size={16} color={active ? GREEN : tok.text.muted} style={{ flexShrink: 0 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </PanelCard>

        {fullMode
          ? <FullInline sections={activeSectionList} scopeEnergy={scopeEnergy} scopeWater={scopeWater} scopeEconomic={scopeEconomic} boundary={liveBoundary} />
          : <SectionPreview sections={activeSectionList} section={selected} scopeEnergy={scopeEnergy} scopeWater={scopeWater} scopeEconomic={scopeEconomic} boundary={liveBoundary} />}
      </Box>

      <FullReport />
    </Box>
  );
};

/* ─────────── Full inline report (on-screen version of the printable report) ─────────── */
function FullInline({ scopeEnergy, scopeWater, scopeEconomic, boundary, sections = sectionList }) {
  const { t, tok } = useReportTok();
  return (
    <M3Surface style={{ paddingTop: 24, paddingBottom: 24 }}>
      <Box sx={{ mb: 2, pb: 1.5, px: 3, borderBottom: `1px solid ${tok.border.card}`, textAlign: "left" }}>
        <Typography sx={{ fontSize: tok.font.size.caption, color: GREEN, fontFamily: tok.font.family, textAlign: "left", fontWeight: 500 }}>
          {t("Circularity Report — Full document")}
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: "20px", fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t(getReportTitle())}</Typography>
        <Typography sx={{ fontSize: tok.font.size.bodyText, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "left" }}>{getProductName()}</Typography>
        <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "left" }}>
          {t("All {{count}} sections rendered continuously — this is what gets exported.", { count: sections.length })}
        </Typography>
      </Box>
      <Box sx={{ display: "grid", gap: 4 }}>
        {sections.map((s, i) => (
          <Box component="section" key={s.key} id={`sec-${s.key}`}>
            <Box sx={{ mb: 1.5, pb: 1, px: 3, display: "flex", alignItems: "baseline", gap: 1.5, borderBottom: `1px solid ${tok.border.card}` }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: "12px", fontWeight: 600, backgroundColor: GREEN, color: "#fff", flexShrink: 0 }}>{i + 1}</Box>
              <Typography sx={{ fontSize: tok.font.size.chartTitle, fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t(renameProductWord(s.label))}</Typography>
              <Typography sx={{ ml: "auto", fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "left" }}>Source · {s.source}</Typography>
            </Box>
            <Box sx={{ px: 0 }}>
              {renderSection(s.key, { scopeEnergy, scopeWater, scopeEconomic, boundary })}
            </Box>
          </Box>
        ))}
      </Box>
    </M3Surface>
  );
}

/* ─────────── PPTX bullet generator ─────────── */
function sectionBullets(k) {
  const totalMass = materials.reduce((s, m) => s + m.mass, 0);
  const weightedMci = materials.reduce((s, m) => s + m.mci * m.mass, 0) / totalMass;
  switch (k) {
    case "product": return [
      `${getProductName()} — Ref ${projectSummary.code}`,
      `${materials.length} materials · ${projectSummary.totalMass.toLocaleString()} kg total mass`,
      `Reference service life: 60 years`,
      `Aligned to ISO 59020:2024 & ISO 59040:2025`,
    ];
    case "scope": return [
      "User type, organizational, temporal & physical boundaries captured",
      "Materiality cut-off 1% by mass; indicators: Material · Energy · Water",
      "Boundary statement auto-generated in Scope & Boundary tab",
    ];
    case "composition": return [
      `Dominant category: aluminium (~${(materials.filter(m => m.category === "Aluminium").reduce((s, m) => s + m.mass, 0) / totalMass * 100).toFixed(0)}% by mass)`,
      `Renewable content by mass: ${(materials.reduce((s, m) => s + m.mass * m.renewablePct / 100, 0) / totalMass * 100).toFixed(1)}%`,
      "See Overview tab for confidence per row",
    ];
    case "flows": return [
      `Virgin: ${(materials.reduce((s, m) => s + m.mass * m.virginPct / 100, 0)).toFixed(0)} kg · Recycled: ${(materials.reduce((s, m) => s + m.mass * m.recycledPct / 100, 0)).toFixed(0)} kg`,
      `Output recovered: ${(materials.reduce((s, m) => s + m.mass * m.eolRecycled / 100, 0)).toFixed(0)} kg`,
      `Combined inflow/outflow per Input + Output sub-tabs`,
    ];
    case "utilityPerf": {
      const e = energySeed[energySeed.length - 1];
      const w = waterSeed[waterSeed.length - 1];
      const rec = ((e.renewableInflow - e.renewableOutflow) / (e.totalInflow - e.totalOutflow)) * 100;
      const cww = (w.circularInflow / w.totalInflow) * 100;
      const mats = getLiveMaterials();
      const avgEff = mats.length > 0 ? mats.reduce((s, r) => s + (r.eol_recycling_efficiency != null ? Number(r.eol_recycling_efficiency) : 90), 0) / mats.length : 90;
      const worstMat = [...mats].sort((a, b) => (Number(b.eol_unrecoverable_waste) || 0) - (Number(a.eol_unrecoverable_waste) || 0))[0];
      return [
        `Utility factor ≈ 1.0 across materials`,
        `Renewable Energy Circularity (${e.year}): ${rec.toFixed(1)}%`,
        `Circular Water Withdrawal (${w.year}): ${cww.toFixed(1)}%`,
        `Economic decoupling flag published from Economic sub-tab`,
        `Average end-of-life recycling efficiency: ${avgEff.toFixed(0)}%`,
        `Highest unrecoverable waste: ${worstMat?.description || "—"}`,
      ];
    }
    case "designAssessment": {
      const mats = getLiveMaterials();
      const excludedSet = new Set(EXCLUDED_TYPES.map(t => t.toLowerCase().trim()));
      const filteredMats = mats.filter(m => {
        const typeVal = String(m.type || "").toLowerCase().trim();
        return !excludedSet.has(typeVal);
      });

      let sumPci = 0;
      let sumFi = 0;
      let sumPciTimesMass = 0;
      let totalDesignMass = 0;
      let validCount = 0;

      filteredMats.forEach((m) => {
        const ct = m.ct_factor !== undefined && m.ct_factor !== null ? parseNumeric(m.ct_factor, 0.8) : 0.8;
        const ca = m.ca_factor !== undefined && m.ca_factor !== null ? parseNumeric(m.ca_factor, 0.8) : 0.8;
        const indep = m.indep_factor !== undefined && m.indep_factor !== null ? parseNumeric(m.indep_factor, 0.4) : 0.4;
        const gpe = m.gpe_factor !== undefined && m.gpe_factor !== null ? parseNumeric(m.gpe_factor, 0.4) : 0.4;
        
        const mciVal = m.percentage_mci !== undefined
          ? Number(m.percentage_mci)
          : (m.MCI !== undefined
            ? Math.max(0, (Number(m.MCI) - 0.1) / 0.9)
            : (m.row_mci !== undefined ? Math.max(0, (Number(m.row_mci) - 0.1) / 0.9) : 0.0));
        const fi = ct * ca * indep * gpe;
        const pci = mciVal * fi;
        const mass = m.mass || 0;

        sumPci += pci;
        sumFi += fi;
        sumPciTimesMass += pci * mass;
        totalDesignMass += mass;
        validCount++;
      });

      const overallPciConnectionWeighted = validCount > 0 ? sumPci / validCount : 0;
      const overallPciMassWeighted = totalDesignMass > 0 ? sumPciTimesMass / totalDesignMass : 0;
      const overallDisassemblyPotential = validCount > 0 ? sumFi / validCount : 0;

      return [
        `Overall PCI (Connection-Weighted): ${overallPciConnectionWeighted.toFixed(2)}`,
        `Overall PCI (Mass-Weighted): ${overallPciMassWeighted.toFixed(2)}`,
        `Disassembly Potential (Fd): ${overallDisassemblyPotential.toFixed(2)}`,
      ];
    }
    case "productMci": return [
      `Weighted MCI: ${weightedMci.toFixed(2)}`,
      `Formula: MCI = 1 − LFI × F(X)`,
      `Worked example uses the highest-mass material`,
      `Material Circularity Indicator: ${projectSummary.projectMci}%`,
      `Highest contributor: ${[...materials].sort((a, b) => b.mci - a.mci)[0].description}`,
      `Held back by: ${[...materials].sort((a, b) => a.mci - b.mci)[0].description}`,
    ];
    case "pciCalculation": return ["PCI Calculation is not yet available for this building."];
    case "sciCalculation": return ["SCI Calculation is not yet available for this building."];
    case "bciCalculation": return ["BCI Calculation is not yet available for this building."];
    case "analytics": return [
      "GWP vs MCI scatter and input feedstock breakdown",
      "Steel wire baskets have the best virgin-to-recycled ratio",
      "Anodised aluminium is the outlier on the GWP vs MCI plot",
    ];
    case "design": {
      const st = designStore.get();
      const { dci, grade, perCriterion } = computeDciFromState(st);
      const weakest = [...designFramework].sort((a, b) => perCriterion[a.key] - perCriterion[b.key])[0];
      return [
        `DCI ${dci.toFixed(0)}/100 · Grade ${grade}`,
        `10 criteria × 5 sub-criteria = 50 indicators`,
        `Top opportunity: ${weakest.label} (${perCriterion[weakest.key]}/25)`,
      ];
    }
    case "benchRecs": return [
      `Position vs industry: above average, below best-in-class`,
      `Highest-priority action: ${recommendations[0].title}`,
      `Roadmap prioritised by MCI-impact per unit of effort`,
    ];
    case "scenario": return [
      "No scenario has been run yet — section marked N/A",
      "Run Scenario / What-if tab to populate",
    ];
    default: return [];
  }
}

/* ─────────── New section components ─────────── */

function DataUnavailable({ note }) {
  const { t, tok } = useReportTok();
  return (
    <Box sx={{ borderRadius: "8px", border: "1px solid #fcd34d", backgroundColor: "#fffbeb", p: 1.5, fontSize: tok.font.size.bodyText, color: "#92400e" }}>
      <Box component="span" sx={{ mr: 1, borderRadius: "999px", backgroundColor: "#fde68a", color: "#78350f", px: 1, py: 0.25, fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {t("Data not yet available")}
      </Box>
      {note}
    </Box>
  );
}
function ScopeSection({ boundary, scopeFlags }) {
  const { t, tok } = useReportTok();
  const flowLabel = boundary.flow === "grave" ? "Cradle-to-grave (A1-C4)" : "Cradle-to-gate (A1-A3)";
  const scopesList = [
    renameProductWord("Material & product circularity"),
    scopeFlags.scopeEnergy && "Energy circularity",
    scopeFlags.scopeWater && "Water circularity",
    scopeFlags.scopeEconomic && "Economic circularity",
  ].filter(Boolean).join(" · ");

  return (
    <Box>
      <KVTable rows={[
        ["User type", "Manufacturer / Assessor"],
        ["Organizational boundary", "Operational control"],
        ["Cut-off rule (reporting-period)", boundary.cutoff],
        ["Physical flow boundary", flowLabel],
        ["Indicator scope", scopesList],
        ["Materiality cut-off", `${(100 - boundary.materiality).toFixed(1)}% residual threshold`],
        ["Standards followed", "ISO 59020:2024, ISO 59040:2025"],
      ]} />
      <SubHeading>{t("Boundary Statement")}</SubHeading>
      <Box sx={{
        border: `1px solid ${tok.border.card}`,
        borderRadius: tok.radius.card,
        backgroundColor: tok.bg.card,
        p: 2,
        mt: 1,
        textAlign: "left",
        display: "flex",
        justifyContent: "flex-start",
      }}>
        <Typography sx={{ fontSize: tok.font.size.bodyText, color: tok.text.primary, fontFamily: tok.font.family, lineHeight: 1.6, textAlign: "left" }}>
          {t("This assessment is prepared under an operational-control organizational boundary, applying a {{cutoff}} residual materiality threshold. The physical flow boundary is {{flow}}, covering {{scopes}}. Calculations are mass-weighted in accordance with ISO 59020:2024 and ISO 59040:2025 conventions.", {
            cutoff: `${(100 - boundary.materiality).toFixed(1)}%`,
            flow: flowLabel.toLowerCase(),
            scopes: scopesList.toLowerCase(),
          })}
        </Typography>
      </Box>
    </Box>
  );
}

function UtilityPerformanceSection({ scopeEnergy, scopeWater, scopeEconomic }) {
  const { t, tok } = useReportTok();
  const e = energySeed[energySeed.length - 1];
  const w = waterSeed[waterSeed.length - 1];
  const ec = economicSeed[economicSeed.length - 1];
  const reCirc = ((e.renewableInflow - e.renewableOutflow) / (e.totalInflow - e.totalOutflow)) * 100;
  const withdrawal = (w.circularInflow / w.totalInflow) * 100;
  const discharge = (w.circularDischarge / w.totalInflow) * 100;
  const reuseRatio = w.waterReused / (w.externalWithdrawal || 1);
  const matGrowth = ((ec.currentMaterialConsumption - ec.previousMaterialConsumption) / (ec.previousMaterialConsumption || 1)) * 100;
  const revGrowth = ((ec.currentRevenue - ec.previousRevenue) / (ec.previousRevenue || 1)) * 100;
  const productivity = ec.currentRevenue / (ec.currentMaterialConsumption || 1);
  const decoupling = matGrowth / (revGrowth || 1);
  const decoupTone = decoupling < 1 ? { bg: "#d1fae5", color: "#065f46" } : decoupling > 1 ? { bg: "#fee2e2", color: "#991b1b" } : { bg: "#fef3c7", color: "#92400e" };
  const decoupLabel = decoupling < 1 ? "Positive decoupling" : decoupling > 1 ? "Negative decoupling" : "Coupled";

  const mats = getLiveMaterials();
  const totalMass = mats.reduce((s, m) => s + (m.mass || 0), 0);
  const avgUf = 1.0;

  const eolRows = useMemo(() => {
    return mats.map((m, idx) => {
      const nm = m.normalized_mass !== undefined && m.normalized_mass !== null 
        ? Number(m.normalized_mass) 
        : (totalMass > 0 ? (m.mass || 0) / totalMass : 0);
      
      const recEffAfterUse = m.eol_recycling_efficiency !== undefined && m.eol_recycling_efficiency !== null
        ? Number(m.eol_recycling_efficiency)
        : 90;
      const recEffRecycledInput = m.eol_feedstock_efficiency !== undefined && m.eol_feedstock_efficiency !== null
        ? Number(m.eol_feedstock_efficiency)
        : 90;

      const omWasteContent = m.om_waste_content !== undefined && m.om_waste_content !== null
        ? Number(m.om_waste_content)
        : calculate_om_waste_content(m);

      const omLandfillWaste = m.om_landfill_waste !== undefined && m.om_landfill_waste !== null
        ? Number(m.om_landfill_waste)
        : calculate_om_landfill_waste({ ...m, normalized_mass: nm, om_waste_content: omWasteContent });

      const recyclingWaste = m.eol_recycling_waste !== undefined && m.eol_recycling_waste !== null
        ? Number(m.eol_recycling_waste)
        : calculate_eol_recycling_waste({ ...m, normalized_mass: nm, eol_recycling_efficiency: recEffAfterUse });

      const feedstockWaste = m.eol_feedstock_waste !== undefined && m.eol_feedstock_waste !== null
        ? Number(m.eol_feedstock_waste)
        : calculate_eol_feedstock_waste({ ...m, normalized_mass: nm, eol_feedstock_efficiency: recEffRecycledInput });

      const unrecoverable = m.eol_unrecoverable_waste !== undefined && m.eol_unrecoverable_waste !== null
        ? Number(m.eol_unrecoverable_waste)
        : calculate_eol_unrecoverable_waste({
            om_landfill_waste: omLandfillWaste,
            eol_recycling_waste: recyclingWaste,
            eol_feedstock_waste: feedstockWaste,
          });

      return {
        id: m.id || m.env_id || `eol-${idx}`,
        description: m.description,
        recEffAfterUse,
        recEffRecycledInput,
        wasteFromRecycling: recyclingWaste,
        unrecoverable,
      };
    });
  }, [mats, totalMass]);

  const avgEolEff = eolRows.length > 0
    ? eolRows.reduce((s, r) => s + r.recEffAfterUse, 0) / eolRows.length
    : 90;
  const sortedEol = [...eolRows].sort((a, b) => b.unrecoverable - a.unrecoverable);
  const worstEol = sortedEol[0] || { description: "None", unrecoverable: 0 };

  return (
    <Box>
      <SubHeading>{t("Utility factor")}</SubHeading>
      <KVTable rows={[
        ["Weighted utility factor", avgUf.toFixed(2)],
        ["Reference lifetime", `${getServiceLife()} years`],
        ["Actual lifetime", `${getServiceLife()} years`],
      ]} />

      {scopeEnergy && (
        <Box sx={{ borderTop: `1px solid ${tok.border.card}`, pt: 1, mt: 2 }}>
          <SubHeading>{t("Energy (ISO 59020 A.8)")}</SubHeading>
          <KVTable rows={[
            ["Reporting year", String(e.year)],
            ["Renewable inflow EIRENE", `${e.renewableInflow.toLocaleString()} MJ`],
            ["Renewable outflow EORENE", `${e.renewableOutflow.toLocaleString()} MJ`],
            ["Total inflow EITE", `${e.totalInflow.toLocaleString()} MJ`],
            ["Total outflow EOTE", `${e.totalOutflow.toLocaleString()} MJ`],
            ["Renewable Energy Circularity", `${reCirc.toFixed(1)}%`],
          ]} />
        </Box>
      )}

      {scopeWater && (
        <Box sx={{ borderTop: `1px solid ${tok.border.card}`, pt: 1, mt: 2 }}>
          <SubHeading>{t("Water (ISO 59020 A.9–A.11)")}</SubHeading>
          <KVTable rows={[
            ["Reporting year", String(w.year)],
            ["Circular Water Withdrawal", `${withdrawal.toFixed(1)}%`],
            ["Circular Water Discharge", `${discharge.toFixed(1)}%`],
            ["Water Reuse Ratio", reuseRatio.toFixed(2)],
          ]} />
        </Box>
      )}

      {scopeEconomic && (
        <Box sx={{ borderTop: `1px solid ${tok.border.card}`, pt: 1, mt: 2 }}>
          <SubHeading>{t("Economic (ISO 59020 A.12–A.13)")}</SubHeading>
          <KVTable rows={[
            ["Reporting year", String(ec.year)],
            ["Revenue", `${ec.revenue.toLocaleString()} CAD`],
            ["Virgin material consumed", `${ec.virginMaterialConsumed} kg`],
            ["Resource growth", `${matGrowth.toFixed(1)}%`],
            ["Economic growth", `${revGrowth.toFixed(1)}%`],
            ["Material productivity", `${productivity.toFixed(0)} CAD / kg`],
            ["Resource intensity index", decoupling.toFixed(2)],
          ]} />
          <Box sx={{ mt: 1, display: "inline-flex" }}>
            <Box component="span" sx={{ borderRadius: "999px", px: 1.5, py: 0.5, fontSize: "12px", fontWeight: 600, backgroundColor: decoupTone.bg, color: decoupTone.color }}>{t(decoupLabel)}</Box>
          </Box>
        </Box>
      )}

      <Box sx={{ borderTop: `1px solid ${tok.border.card}`, pt: 1, mt: 2 }}>
        <SubHeading>{t("End-of-Life Performance")}</SubHeading>
        <DataTable
          columns={[
            { key: "description", label: "Material" },
            { key: "recEffAfterUse", label: "Rec. eff. after use", align: "right", render: (r) => `${r.recEffAfterUse.toFixed(0)}%` },
            { key: "recEffRecycledInput", label: "Rec. eff. of recycled input", align: "right", render: (r) => `${r.recEffRecycledInput.toFixed(0)}%` },
            { key: "wasteFromRecycling", label: "Waste from recycling", align: "right", render: (r) => `${r.wasteFromRecycling.toFixed(2)}%` },
            { key: "unrecoverable", label: "Unrecoverable", align: "right", render: (r) => `${r.unrecoverable.toFixed(2)}%` },
          ]}
          rows={eolRows}
          getKey={(r) => r.id}
        />
        <Typography sx={{ fontSize: tok.font.size.bodyText, color: tok.text.secondary, fontFamily: tok.font.family, mt: 1.5, textAlign: "left" }}>
          {t("Average recycling efficiency after use is {{avg}}%. The largest unrecoverable-waste contributor is {{worst}} at {{pct}}%.", {
            avg: avgEolEff.toFixed(0), worst: worstEol.description, pct: worstEol.unrecoverable.toFixed(1),
          })}
        </Typography>
      </Box>
    </Box>
  );
}

function BenchRecsSection() {
  const { t, tok, isDark } = useReportTok();
  void inputMaterialSeed; void outputMaterialSeed;
  const top = recommendations[0];
  return (
    <Box>
      <SubHeading>{t("Benchmark")}</SubHeading>
      <Box className="avoid-break" sx={{ height: 208 }}>
        <ResponsiveContainer>
          <BarChart data={benchmarks} layout="vertical" margin={{ left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" domain={[0, 100]} fontSize={11} />
            <YAxis type="category" dataKey="label" fontSize={11} width={110} />
            <Tooltip />
            <Bar dataKey="mci" fill="#7dbf3f" />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <SubHeading>{t("Recommendations (ranked by impact / effort)")}</SubHeading>
      <Box sx={{ display: "grid", gap: 1 }}>
        {[...recommendations].sort((a, b) => {
          const w = (r) => (r.effort === "Low" ? 3 : r.effort === "Medium" ? 2 : 1);
          return w(b) - w(a);
        }).map((r) => (
          <Box key={r.title} sx={{ borderRadius: "8px", border: `1px solid ${tok.border.card}`, backgroundColor: tok.bg.card, p: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontWeight: 500, color: tok.text.primary, fontFamily: tok.font.family }}>{r.title}</Typography>
              <Box component="span" sx={{ borderRadius: "999px", px: 1, py: 0.25, fontSize: "11px", fontWeight: 600, backgroundColor: isDark ? "rgba(101,157,51,0.20)" : "rgba(101,157,51,0.12)", color: GREEN }}>
                {r.impact} · {r.effort} {t("effort")}
              </Box>
            </Box>
            <Typography sx={{ mt: 0.5, fontSize: tok.font.size.bodyText, color: tok.text.muted, fontFamily: tok.font.family }}>{r.detail}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ScenarioSection() {
  return (
    <Box>
      <DataUnavailable note="No scenario has been run yet. Use the Scenario / What-if tab to model lever changes, then this section will populate with the settings and resulting trade-off." />
    </Box>
  );
}

function DppSection() {
  const { t, tok } = useReportTok();
  const payload = encodeURIComponent(JSON.stringify({
    product: getProductName(),
    code: projectSummary.code,
    mci: projectSummary.projectMci,
    gwp: projectSummary.totalGwp,
    materials: materials.length,
  }));
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${payload}`;
  return (
    <Box>
      <ExecSummary>
        {t("A Digital Product Passport (DPP) payload has been generated combining product identity, MCI, GWP, composition and end-of-life guidance. The payload is EU DPP-draft and ISO 59040 PCDS compatible.")}
      </ExecSummary>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "220px 1fr" } }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, borderRadius: "12px", border: `1px solid ${tok.border.card}`, backgroundColor: tok.bg.card, p: 2 }}>
          <Box component="img" src={qr} width={180} height={180} alt="DPP QR code" sx={{ borderRadius: "6px", backgroundColor: "#fff", p: 1 }} />
          <Typography sx={{ fontSize: "11px", color: tok.text.muted, fontFamily: tok.font.family }}>{t("Scan for passport")}</Typography>
        </Box>
        <KVTable rows={[
          ["Product", getProductName()],
          ["Reference code", projectSummary.code],
          ["Materials", `${materials.length} items · ${projectSummary.totalMass.toLocaleString()} kg`],
          ["Circularity indicators", `Project MCI ${projectSummary.projectMci}%`],
          ["EPD reference", "EPD-ARI-000482 (in preparation)"],
          ["Carbon footprint", `${projectSummary.totalGwp.toLocaleString()} t CO₂e (cradle-to-gate)`],
          ["Repair information", "Repair guide v2.1 · standard M-series fasteners · 10-year spare parts"],
          ["Recycling guidance", "Aluminium → dedicated smelter; straw → composting or biomass energy"],
        ]} />
      </Box>
    </Box>
  );
}
void DppSection; // referenced only for parity with the reference file (unused there too — never wired into sectionList/FullReport)

/* ─────────── Section preview (interactive right pane) ─────────── */

function SectionPreview({ section, scopeEnergy, scopeWater, scopeEconomic, boundary, sections = sectionList }) {
  const { t, tok } = useReportTok();
  const meta = sections.find((s) => s.key === section);
  return (
    <M3Surface style={{ paddingTop: 24, paddingBottom: 24 }}>
      <Box sx={{ mb: 2, pb: 1.5, px: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, borderBottom: `1px solid ${tok.border.card}` }}>
        <Box sx={{ textAlign: "left" }}>
          <Typography sx={{ fontSize: tok.font.size.caption, color: GREEN, fontFamily: tok.font.family, textAlign: "left", fontWeight: 500 }}>{t("Circularity Report — Preview")}</Typography>
          <Typography sx={{ mt: 0.5, fontSize: "20px", fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t(renameProductWord(meta.label))}</Typography>
          <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "left" }}>{t(meta.blurb)}</Typography>
        </Box>
        <Box sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family }}>{t(getReportTitle())}</Typography>
          <Typography sx={{ fontSize: "11px", color: tok.text.muted, fontFamily: tok.font.family }}>{getProductName()}</Typography>
        </Box>
      </Box>
      <Box sx={{ px: 0 }}>
        {renderSection(section, { scopeEnergy, scopeWater, scopeEconomic, boundary })}
      </Box>
    </M3Surface>
  );
}

const EXCLUDED_TYPES = [
  "Building Service Engineering",
  "Processes at Building Site",
  "End of Life",
  "Energy sources",
  "Transport",
  "Chemicals",
  "Refrigerants",
  "Water"
];

const parseNumeric = (val, defaultVal = 0) => {
  if (val === undefined || val === null) return defaultVal;
  if (typeof val === "number") return val;
  const parsed = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
  return isNaN(parsed) ? defaultVal : parsed;
};

function DesignAssessmentSection() {
  const { t } = useReportTok();
  const mats = getLiveMaterials();

  const computed = useMemo(() => {
    let totalMass = 0;
    let sumPciTimesMass = 0;
    let sumFiTimesMass = 0;
    let sumPci = 0;
    let sumFi = 0;
    let validCount = 0;

    const excludedSet = new Set(EXCLUDED_TYPES.map(t => t.toLowerCase().trim()));
    const filteredMats = mats.filter(m => {
      const typeVal = String(m.type || "").toLowerCase().trim();
      return !excludedSet.has(typeVal);
    });

    const rows = filteredMats.map((m) => {
      const ct = m.ct_factor !== undefined && m.ct_factor !== null ? parseNumeric(m.ct_factor, 0.8) : 0.8;
      const ca = m.ca_factor !== undefined && m.ca_factor !== null ? parseNumeric(m.ca_factor, 0.8) : 0.8;
      const indep = m.indep_factor !== undefined && m.indep_factor !== null ? parseNumeric(m.indep_factor, 0.4) : 0.4;
      const gpe = m.gpe_factor !== undefined && m.gpe_factor !== null ? parseNumeric(m.gpe_factor, 0.4) : 0.4;
      
      const mciVal = m.percentage_mci !== undefined
        ? Number(m.percentage_mci)
        : (m.MCI !== undefined
          ? Math.max(0, (Number(m.MCI) - 0.1) / 0.9)
          : (m.row_mci !== undefined ? Math.max(0, (Number(m.row_mci) - 0.1) / 0.9) : 0.0));
      const fi = ct * ca * indep * gpe;
      const pci = mciVal * fi;
      const mass = m.mass || 0;

      totalMass += mass;
      sumPciTimesMass += pci * mass;
      sumFiTimesMass += fi * mass;
      sumPci += pci;
      sumFi += fi;
      validCount++;

      return {
        ...m,
        ct,
        ca,
        indep,
        gpe,
        mciVal,
        fi,
        pci,
        mass
      };
    });

    return { rows };
  }, [mats]);

  const { rows } = computed;

  return (
    <Box>
      <SubHeading>{t("Connection and Disassembly Factors Breakdown")}</SubHeading>
      <DataTable
        getKey={(m) => m.id || m.description}
        columns={[
          { key: "description", label: t("Description") },
          { key: "mciVal", label: t("MCIp"), align: "right", render: (r) => r.mciVal.toFixed(2) },
          { key: "ct", label: t("CT"), align: "right", render: (r) => r.ct.toFixed(1) },
          { key: "ca", label: t("CA"), align: "right", render: (r) => r.ca.toFixed(1) },
          { key: "indep", label: t("ID"), align: "right", render: (r) => r.indep.toFixed(1) },
          { key: "gpe", label: t("GPE"), align: "right", render: (r) => r.gpe.toFixed(1) },
          { key: "pci", label: t("PCI"), align: "right", render: (r) => r.pci.toFixed(2) },
        ]}
        rows={rows}
      />
    </Box>
  );
}

function renderSection(k, scopeFlags = {}) {
  const boundary = scopeFlags.boundary || {
    start: "2026-01-01",
    end: "2026-12-31",
    cutoff: "Fiscal year-end",
    flow: "gate",
    materiality: 95,
  };
  switch (k) {
    case "product": return <Box sx={{ px: 3 }}><ProductInfoSection /></Box>;
    case "scope": return <Box sx={{ px: 3 }}><ScopeSection boundary={boundary} scopeFlags={scopeFlags} /></Box>;
    case "composition": return <Box sx={{ px: 3 }}><CompositionSection /></Box>;
    case "flows": return <Box sx={{ px: 3 }}><FlowsSection /></Box>;
    case "utilityPerf": return <Box sx={{ px: 3 }}><UtilityPerformanceSection {...scopeFlags} /></Box>;
    case "designAssessment": return <Box sx={{ px: 3 }}><DesignAssessmentSection /></Box>;
    case "productMci": return <Box sx={{ px: 3 }}><ProductMciSection /></Box>;
    case "pciCalculation": return <Box sx={{ px: 3 }}><DataUnavailable note="PCI Calculation for this building is not yet available." /></Box>;
    case "sciCalculation": return <Box sx={{ px: 3 }}><DataUnavailable note="SCI Calculation for this building is not yet available." /></Box>;
    case "bciCalculation": return <Box sx={{ px: 3 }}><DataUnavailable note="BCI Calculation for this building is not yet available." /></Box>;
    case "analytics": return <Box sx={{ px: 3 }}><AnalyticsSection /></Box>;
    case "design": return <Box sx={{ px: 3 }}><DesignSection /></Box>;
    case "benchRecs": return <Box sx={{ px: 3 }}><BenchRecsSection /></Box>;
    case "scenario": return <Box sx={{ px: 3 }}><ScenarioSection /></Box>;
    default: return null;
  }
}

/* ─────────── Shared UI atoms ─────────── */

function ExecSummary({ children }) {
  const { tok, isDark } = useReportTok();
  return (
    <Box sx={{ mb: 2, borderRadius: tok.radius.card, borderLeft: `3px solid ${GREEN}`, backgroundColor: isDark ? "rgba(101,157,51,0.08)" : "rgba(101,157,51,0.05)", p: 2, textAlign: "left" }}>
      <Typography sx={{ mt: 0.5, fontSize: tok.font.size.bodyText, lineHeight: 1.7, color: tok.text.secondary, fontFamily: tok.font.family, fontWeight: 400, textAlign: "left" }}>{children}</Typography>
    </Box>
  );
}
function KpiGrid({ items }) {
  const { tok } = useReportTok();
  return (
    <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr 1fr", lg: `repeat(${items.length}, 1fr)` } }}>
      {items.map((k) => (
        <Box key={k.label} sx={{ borderRadius: tok.radius.card, border: `1px solid ${tok.border.card}`, backgroundColor: tok.bg.card, p: 2, textAlign: "left" }}>
          <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "left", fontWeight: 500 }}>{k.label}</Typography>
          <Typography sx={{ fontSize: "18px", fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{k.value}</Typography>
          {k.sub && <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "left" }}>{k.sub}</Typography>}
        </Box>
      ))}
    </Box>
  );
}
function KVTable({ rows }) {
  const colDefs = useMemo(() => [
    {
      headerName: "Attribute",
      field: "field",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 200,
      flex: 1,
      cellStyle: {
        ...leftAlignColDef.cellStyle,
        fontSize: "14px",
      },
    },
    {
      headerName: "Value",
      field: "value",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 220,
      flex: 2,
    },
  ], []);

  const rowData = useMemo(() => rows.map(([k, v], i) => ({ id: i, field: k, value: v })), [rows]);

    return (
      <Box sx={{ my: 1, mx: -3 }}>
        <AgGrid
          columnDefs={colDefs}
          rowData={rowData}
          getRowId={(params) => String(params.data.id)}
          domLayout="autoHeight"
          suppressPagination
          headerCheckbox={false}
          checkboxes={false}
        />
      </Box>
    );
}
function SubHeading({ children }) {
  const { tok } = useReportTok();
  return <Typography sx={{ mt: 2.5, mb: 1, fontSize: tok.font.size.sectionTitle, fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{children}</Typography>;
}
function DataTable({ columns, rows, getKey }) {
  const colDefs = useMemo(() => columns.map((c) => ({
    headerName: c.label,
    field: c.key,
    ...(c.align === "right" ? rightAlignColDef : leftAlignColDef),
    ...defaultColDef,
    minWidth: 140,
    flex: 1,
    ...(c.render ? { cellRenderer: (p) => c.render(p.data) } : {}),
  })), [columns]);

  return (
    <Box sx={{ my: 1, mx: -3 }}>
      <AgGrid
        columnDefs={colDefs}
        rowData={rows}
        getRowId={(params) => String(getKey(params.data))}
        domLayout="autoHeight"
        suppressPagination
        headerCheckbox={false}
        checkboxes={false}
      />
    </Box>
  );
}

/* ─────────── Derived data ─────────── */

function useDerived() {
  return useMemo(() => {
    const totalMass = materials.reduce((s, m) => s + m.mass, 0);
    const virginMass = materials.reduce((s, m) => s + m.mass * m.virginPct / 100, 0);
    const recycledMass = materials.reduce((s, m) => s + m.mass * m.recycledPct / 100, 0);
    const reusedMass = materials.reduce((s, m) => s + m.mass * m.reusedPct / 100, 0);
    const renewableMass = materials.reduce((s, m) => s + m.mass * m.renewablePct / 100, 0);
    const recycledOut = materials.reduce((s, m) => s + m.mass * m.eolRecycled / 100, 0);
    const reusedOut = materials.reduce((s, m) => s + m.mass * m.eolReused / 100, 0);
    const landfillOut = materials.reduce((s, m) => s + m.mass * m.eolLandfill / 100, 0);
    const incinOut = materials.reduce((s, m) => s + m.mass * m.eolIncinerated / 100, 0);
    const weightedMci = materials.reduce((s, m) => s + m.mci * m.mass, 0) / totalMass;
    const weightedLfi = materials.reduce((s, m) => s + m.lfi * m.mass, 0) / totalMass;
    return {
      totalMass, virginMass, recycledMass, reusedMass, renewableMass,
      recycledOut, reusedOut, landfillOut, incinOut, weightedMci, weightedLfi,
    };
  }, []);
}

/* ─────────── Sections ─────────── */

function ProductInfoSection() {
  const { t } = useReportTok();
  const today = new Date().toLocaleDateString("en-CA");
  const mciPct = getLiveMciPct();
  const massSum = getLiveMassSum();
  return (
    <Box>
      <KVTable rows={[
        [renameProductWord("Product name"), getProductName()],
        [renameProductWord("Product ID"), getProductId()],
        [renameProductWord("Product category"), getSubCategory()],
        ["Functional unit", `${getServiceLife()} years service life`],
        ["Reference service life", `${getServiceLife()} years`],
        [renameProductWord("Total product mass"), `${(typeof massSum === 'number' ? massSum : 0).toFixed(2)} kg`],
        [renameProductWord("Material Circularity Indicator"), `${(typeof mciPct === 'number' ? mciPct : 0).toFixed(1)}%`],
        ["Standards followed", "ISO 59020:2024, ISO 59040:2025, EN 15804+A2"],
        ["Report generation date", today],
      ]} />
    </Box>
  );
}

function CompositionSection() {
  const { t } = useReportTok();
  const mats = getLiveMaterials();
  const totalMass = mats.reduce((s, m) => s + (m.mass || 0), 0);
  const virginMass = mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_virgin_content || 0) / 100), 0);
  const recycledMass = mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_recycled_content || 0) / 100), 0);
  const reusedMass = mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_reuse || 0) / 100), 0);
  const totalGwp = mats.reduce((s, m) => s + (m.gwp_total || 0), 0);
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(5, 1fr)" }, gap: 2, mb: 3 }}>
        <MetricCard title={t("Virgin")} subtitle={`${virginMass.toFixed(2)} kg`} value={totalMass > 0 ? virginMass / totalMass * 100 : 0} unit="%" icon={LuBox} accent="#ea580c" bg="rgba(234, 88, 12, 0.1)" variant="stage" />
        <MetricCard title={t("Recycled")} subtitle={`${recycledMass.toFixed(2)} kg`} value={totalMass > 0 ? recycledMass / totalMass * 100 : 0} unit="%" icon={LuRecycle} accent="#16a34a" bg="rgba(22, 163, 74, 0.1)" variant="stage" />
        <MetricCard title={t("Reused")} subtitle={`${reusedMass.toFixed(2)} kg`} value={totalMass > 0 ? reusedMass / totalMass * 100 : 0} unit="%" icon={LuRefreshCw} accent="#8b5cf6" bg="rgba(139, 92, 246, 0.1)" variant="stage" />
        <MetricCard title={t("Total Mass")} subtitle="kg" value={totalMass.toFixed(2)} unit="kg" icon={LuLeaf} accent="#0d9488" bg="rgba(13, 148, 136, 0.1)" variant="stage" />
        <MetricCard title={t("Total GWP")} subtitle="kg CO₂e" value={totalGwp.toFixed(2)} unit="kg CO₂e" icon={LuCloud} accent="#2563eb" bg="rgba(37, 99, 235, 0.1)" variant="stage" />
      </Box>
      <SubHeading>{t("Material breakdown")}</SubHeading>
      <DataTable
        getKey={(m) => m.id || m.description}
        columns={[
          { key: "description", label: "Material" },
          { key: "category", label: "Category" },
          { key: "sub_category", label: "Sub Category" },
          { key: "mass", label: "Mass (kg)", align: "right", render: (m) => (m.mass || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }) },
          { key: "pctMass", label: "% Mass", align: "right", render: (m) => totalMass > 0 ? ((m.mass || 0) / totalMass * 100).toFixed(2) + "%" : "0.00%" },
          { key: "gwp_total", label: "GWP (kg CO₂e)", align: "right", render: (m) => (m.gwp_total || 0).toFixed(2) },
        ]}
        rows={mats}
      />
    </Box>
  );
}

function FlowsSection() {
  const { t, tok } = useReportTok();
  const mats = getLiveMaterials();
  const totalMass = mats.reduce((s, m) => s + (m.mass || 0), 0);
  const virginMass = mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_virgin_content || 0) / 100), 0);
  const recycledInflow = mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_recycled_content || 0) / 100), 0);
  const recycledOut = mats.reduce((s, m) => s + (m.mass || 0) * ((m.eol_recycling || m.om_recycling || 0) / 100), 0);
  const barData = [
    { name: "Virgin inflow", value: +virginMass.toFixed(1), fill: "#e5766a" },
    { name: "Recycled inflow", value: +recycledInflow.toFixed(1), fill: "#7ed07e" },
    { name: "Recovered output", value: +recycledOut.toFixed(1), fill: "#7fb5e6" },
    { name: "Loss / waste", value: +(totalMass - recycledOut).toFixed(1) > 0 ? +(totalMass - recycledOut).toFixed(1) : 0, fill: "#c58bce" },
  ];
  return (
    <Box>
      <Typography sx={{ fontSize: tok.font.size.bodyText, color: tok.text.secondary, fontFamily: tok.font.family, mb: 2, textAlign: "left" }}>
        {t("This section presents the physical flows consolidated from the circularity database records. Material inflows map the raw feedstock inputs (virgin vs. recycled ratio), and outflows track expected recovery pathways (recycling efficiency or end-of-life collection potential).")}
      </Typography>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
        <Box>
          <SubHeading>{t("Inflows")}</SubHeading>
          <KVTable rows={[
            ["Primary (virgin)", `${virginMass.toFixed(1)} kg`],
            ["Secondary (recycled)", `${recycledInflow.toFixed(1)} kg`],
            ["Total inflow", `${totalMass.toFixed(1)} kg`],
          ]} />
        </Box>
        <Box>
          <SubHeading>{t("Outflows")}</SubHeading>
          <KVTable rows={[
            ["Recovered output", `${recycledOut.toFixed(1)} kg`],
            ["Waste / loss", `${(totalMass - recycledOut).toFixed(1)} kg`],
            ["Recovery rate", totalMass > 0 ? `${(recycledOut / totalMass * 100).toFixed(1)}%` : "—"],
          ]} />
        </Box>
      </Box>
      <SubHeading>{t("Inflow vs. Outflow")}</SubHeading>
      <Box sx={{ height: 220 }}>
        <ResponsiveContainer>
          <BarChart data={barData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal={false} />
            <XAxis type="number" fontSize={12} fontFamily={tok.font.family} tick={{ fill: tok.text.muted }} unit=" kg" />
            <YAxis type="category" dataKey="name" fontSize={12} fontFamily={tok.font.family} tick={{ fill: tok.text.primary }} width={130} />
            <Tooltip
              contentStyle={{ fontFamily: tok.font.family, fontSize: 12, borderRadius: 8, border: `1px solid ${tok.border.card}` }}
              formatter={(v) => [`${v} kg`]}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {barData.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function UtilitySection() {
  const { t, tok } = useReportTok();
  const mats = getLiveMaterials();
  const totalMass = mats.reduce((s, m) => s + (m.mass || 0), 0);
  const recycledOut = mats.reduce((s, m) => s + (m.mass || 0) * ((m.eol_recycling || m.om_recycling || 0) / 100), 0);
  const lossOut = totalMass - recycledOut > 0 ? totalMass - recycledOut : 0;
  const rows = mats.map((m) => {
    const lifeVal = m.uf_product_life !== undefined && m.uf_product_life !== null && !isNaN(Number(m.uf_product_life))
      ? Number(m.uf_product_life)
      : getServiceLife();
    const indVal = m.uf_industry_avg_life !== undefined && m.uf_industry_avg_life !== null && !isNaN(Number(m.uf_industry_avg_life))
      ? Number(m.uf_industry_avg_life)
      : getServiceLife();
    const ufVal = m.uf_calculations !== undefined && m.uf_calculations !== null && !isNaN(Number(m.uf_calculations))
      ? Number(m.uf_calculations)
      : (indVal > 0 ? lifeVal / indVal : 1.0);
    return {
      name: m.description,
      life: lifeVal,
      ind: indVal,
      uf: ufVal,
    };
  });
  const barData = [
    { name: "Recycled/Recovered", value: +recycledOut.toFixed(1), fill: "#7ed07e" },
    { name: "Waste/Loss", value: +lossOut.toFixed(1), fill: "#c58bce" },
  ];
  return (
    <Box>
      <KVTable rows={[
        ["Reference lifetime", `${getServiceLife()} years`],
        ["Actual lifetime", `${getServiceLife()} years`],
        ["Maintenance frequency", "Annual visual inspection"],
        ["Repairability", "High"],
        ["Upgradeability", "Medium"],
        ["Reusability", "High"],
      ]} />
      <SubHeading>{t("Per-material utility")}</SubHeading>
      <DataTable
        getKey={(r) => r.name}
        columns={[
          { key: "name", label: "Material" },
          { key: "life", label: "Life (yrs)", align: "right" },
          { key: "ind", label: "Reference", align: "right" },
          { key: "uf", label: "Utility factor", align: "right", render: (r) => r.uf.toFixed(2) },
        ]}
        rows={rows}
      />

      <SubHeading>{t("End-of-Life Outflow Distribution")}</SubHeading>
      <Box sx={{ height: 180 }}>
        <ResponsiveContainer>
          <BarChart data={barData} margin={{ top: 12, right: 24, left: 8, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="name" fontSize={11} fontFamily={tok.font.family} tick={{ fill: tok.text.primary }} />
            <YAxis type="number" fontSize={11} fontFamily={tok.font.family} tick={{ fill: tok.text.muted }} unit=" kg" />
            <Tooltip
              contentStyle={{ fontFamily: tok.font.family, fontSize: 12, borderRadius: 8, border: `1px solid ${tok.border.card}` }}
              formatter={(v) => [`${v} kg`]}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {barData.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function ProductMciSection() {
  const { t, tok } = useReportTok();
  const mats = getLiveMaterials();
  const score = getLiveMciPct().toFixed(1);
  const totalMass = mats.reduce((s, m) => s + (m.mass || 0), 0);
  const weightedMci = totalMass > 0
    ? mats.reduce((s, m) => s + (m.row_mci || 0) * (m.mass || 0), 0) / totalMass
    : 0;
  return (
    <Box>
      <SubHeading>{t("Material Circularity Indicator")}</SubHeading>
      <DataTable
        getKey={(m) => m.id || m.description}
        columns={[
          { key: "description", label: "Material" },
          { key: "im_virgin_content", label: "Virgin %", align: "right", render: (m) => `${(m.im_virgin_content || 0).toFixed(0)}%` },
          { key: "im_recycled_content", label: "Recycled %", align: "right", render: (m) => `${(m.im_recycled_content || 0).toFixed(0)}%` },
          { key: "eol_recycling_efficiency", label: "Recycling eff.", align: "right", render: (m) => `${(m.eol_recycling_efficiency || 0).toFixed(0)}%` },
          { key: "row_mci", label: "MCI", align: "right", render: (m) => ((m.row_mci || 0) * 100).toFixed(1) + "%" },
        ]}
        rows={mats}
      />

      <Box sx={{ borderTop: `1px solid ${tok.border.card}`, pt: 1, mt: 3 }}>
        <SubHeading>{t("Material Circularity Indicator Summary")}</SubHeading>
        <Typography sx={{ fontSize: tok.font.size.bodyText, color: tok.text.secondary, fontFamily: tok.font.family, mb: 2, textAlign: "left" }}>
          {t("Overall Material Circularity Indicator: {{score}}%. Weighted-average score across all materials.", { score })}
        </Typography>
        <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "200px 1fr" }, mb: 1, alignItems: "center" }}>
          <Box sx={{ borderRadius: tok.radius.card, border: `1px solid ${tok.border.card}`, backgroundColor: "rgba(101,157,51,0.08)", p: 3, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family, fontWeight: 500 }}>{t("Material Circularity Indicator")}</Typography>
            <Typography sx={{ mt: 1, fontSize: "40px", fontWeight: 700, color: tok.text.primary, fontFamily: tok.font.family }}>{score}%</Typography>
            <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family }}>{t("Weighted, mass-based")}</Typography>
          </Box>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "13px", color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>
              {t("Component-wise Circularity Progress")}
            </Typography>
            {mats.map((m) => (
              <Box key={m.id || m.description} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography sx={{ width: 180, flexShrink: 0, fontSize: "11px", fontWeight: 500, color: tok.text.primary, fontFamily: tok.font.family, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" }}>
                  {m.description}
                </Typography>
                <Box sx={{ height: 10, flex: 1, borderRadius: "5px", backgroundColor: tok.border.card, overflow: "hidden" }}>
                  <Box sx={{ height: "100%", borderRadius: "5px", backgroundColor: GREEN, width: `${((m.row_mci || 0) * 100).toFixed(0)}%`, transition: "width 0.4s ease-in-out" }} />
                </Box>
                <Typography sx={{ width: 45, textAlign: "right", fontSize: "11px", fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family }}>
                  {((m.row_mci || 0) * 100).toFixed(1)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function AnalyticsSection() {
  const { t } = useReportTok();
  const mats = getLiveMaterials();
  const mciPct = getLiveMciPct();
  const massSum = getLiveMassSum();
  const gwpSum = getLiveGwpSum();
  const totalMass = mats.reduce((s, m) => s + (m.mass || 0), 0);
  const recycledOut = mats.reduce((s, m) => s + (m.mass || 0) * ((m.eol_recycling || m.om_recycling || 0) / 100), 0);

  const pciData = useMemo(() => {
    return computePciData(liveCombinedData, getTopic(), getProductId());
  }, []);

  const hasPci = (pciData?.percentageOverallPci || 0) > 0;

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
        <MetricCard title={t("Material Circularity Indicator")} value={mciPct.toFixed(2)} unit="%" icon={LuGauge} accent="#16a34a" bg="rgba(22, 163, 74, 0.1)" variant="stage" />
        <MetricCard title={t(renameProductWord("Product Circularity Indicator"))} value={(pciData?.percentageOverallPci || 0).toFixed(2)} unit="%" icon={LuBox} accent="#0d9488" bg="rgba(13, 148, 136, 0.1)" variant="stage" />
        <MetricCard title={t("Embodied carbon")} value={(gwpSum || 0).toFixed(2)} unit="kg CO₂e" icon={LuCloud} accent="#2563eb" bg="rgba(37, 99, 235, 0.1)" variant="stage" />
        <MetricCard title={t("Recovery rate")} value={totalMass > 0 ? (recycledOut / totalMass * 100).toFixed(2) : 0} unit="%" icon={LuRecycle} accent="#ea580c" bg="rgba(234, 88, 12, 0.1)" variant="stage" />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", flexDirection: "column", gap: 3 }}>
        {hasPci ? (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
              <Box sx={{ minHeight: 450, height: "auto" }}>
                <CircularChart title="Material Circularity Indicator" topic={getTopic()} combined_data={liveCombinedData} />
              </Box>
              <Box sx={{ minHeight: 450, height: "auto" }}>
                <CircularPCIChart title={renameProductWord("Product Circularity Indicator")} topic={getTopic()} combined_data={liveCombinedData} productId={getProductId()} />
              </Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
              <Box sx={{ minHeight: 420, height: "auto" }}>
                <NomarlizedGwpVsPercentageMciChart topic={getTopic()} combined_data={liveCombinedData} />
              </Box>
              <Box sx={{ minHeight: 420, height: "auto" }}>
                <NomarlizedGwpVsPercentagePciChart topic={getTopic()} combined_data={liveCombinedData} productId={getProductId()} />
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            <Box sx={{ minHeight: 450, height: "auto" }}>
              <CircularChart title="Material Circularity Indicator" topic={getTopic()} combined_data={liveCombinedData} />
            </Box>
            <Box sx={{ minHeight: 420, height: "auto" }}>
              <NomarlizedGwpVsPercentageMciChart topic={getTopic()} combined_data={liveCombinedData} />
            </Box>
          </Box>
        )}
        <Box sx={{ minHeight: 370, height: "auto" }}>
          <InputMaterialChart height={450} circularities={liveCombinedData?.combined_circularities || []} />
        </Box>
        <Box sx={{ minHeight: 370, height: "auto" }}>
          <OutputMaterialChart height={450} circularities={liveCombinedData?.combined_circularities || []} />
        </Box>
      </Box>
    </Box>
  );
}

function DesignSection() {
  const { t, tok } = useReportTok();
  const state = useSyncExternalStore(designStore.subscribe, designStore.get, designStore.get);
  const { perCriterion, total, dci, grade } = computeDciFromState(state);
  const info = dciGradeInfo[grade];
  const radar = designFramework.map((c) => ({ criterion: c.label, score: perCriterion[c.key] / 5 }));
  const sorted = [...designFramework].sort((a, b) => perCriterion[a.key] - perCriterion[b.key]);
  const weakest = sorted[0];
  return (
    <Box>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "220px 1fr" } }}>
        <Box sx={{ borderRadius: tok.radius.card, border: `1px solid ${tok.border.card}`, backgroundColor: "rgba(125,191,63,0.10)", p: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family, fontWeight: 500 }}>{t("Design Circularity Index")}</Typography>
          <Typography sx={{ mt: 1, fontSize: "48px", fontWeight: 700, color: tok.text.primary, fontFamily: tok.font.family }}>{grade}</Typography>
          <Typography sx={{ fontSize: "13px", color: tok.text.muted, fontFamily: tok.font.family }}>{dci.toFixed(0)}/100 · {total}/250 pts</Typography>
          <Typography sx={{ mt: 1, fontSize: "10px", color: tok.text.muted, fontFamily: tok.font.family }}>{t("Inspired by ISO 59020/59004 — internal reporting convention.")}</Typography>
        </Box>
        <Box className="avoid-break" sx={{ height: 224 }}>
          <ResponsiveContainer>
            <RadarChart data={radar} outerRadius={80}>
              <PolarGrid />
              <PolarAngleAxis dataKey="criterion" fontSize={11} />
              <PolarRadiusAxis domain={[0, 5]} fontSize={10} />
              <Radar dataKey="score" stroke="#7dbf3f" fill="#7dbf3f" fillOpacity={0.5} />
            </RadarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
      <SubHeading>{t("Per-criterion scores")}</SubHeading>
      <DataTable
        getKey={(c) => c.key}
        columns={[
          { key: "label", label: "Criterion" },
          { key: "score", label: "Score / 25", align: "right", render: (c) => perCriterion[c.key] },
          { key: "notes", label: "Evidence / Notes", render: (c) => state.notes[c.key] || "—" },
        ]}
        rows={designFramework}
      />
    </Box>
  );
}

function RenewableSection() {
  const { t } = useReportTok();
  const d = useDerived();
  const renewablePct = d.renewableMass / d.totalMass * 100;
  return (
    <Box>
      <ExecSummary>
        {t("Renewable content sits at {{pct}}% by mass, driven almost entirely by straw insulation. Renewable energy in manufacturing is not yet fully substituted.", { pct: renewablePct.toFixed(1) })}
      </ExecSummary>
      <KpiGrid items={[
        { label: "Renewable material", value: `${renewablePct.toFixed(1)}%` },
        { label: "Renewable feedstock", value: `${materials.filter((m) => m.renewablePct > 0).length}/${materials.length}` },
        { label: "Biomass content", value: `${d.renewableMass.toFixed(0)} kg` },
        { label: "Renewable energy", value: "~28% (grid mix)" },
      ]} />
      <KVTable rows={[
        ["Certification status", "Straw batch traceable to FSC-adjacent farm registry (pilot)"],
        ["Renewable energy contribution", "Site electricity ~28% renewable; ambition 100% by 2028"],
        ["Biobased carbon storage", "Straw core sequesters ~1.1 kg CO₂e / kg over service life"],
      ]} />
    </Box>
  );
}

function AiMatchSection() {
  const { t } = useReportTok();
  const matches = [
    { current: "Aluminium Profile (Anodized) – 40% recycled", suggestion: "Hydro CIRCAL 75R anodised profile", benefit: "−32% GWP", mci: "+0.14 MCI", cost: "+3–5% cost" },
    { current: "Straw Insulation", suggestion: "Certified Ecococon straw panel", benefit: "Verified biobased carbon", mci: "+0.05 MCI", cost: "Cost-neutral" },
    { current: "Powder-coated aluminium", suggestion: "PPG Envirocron LEED-compliant coating on secondary Al", benefit: "−18% coating GWP", mci: "+0.09 MCI", cost: "Negligible" },
    { current: "Annealed wire rod", suggestion: "EAF-route rebar equivalent, ≥90% scrap", benefit: "−45% GWP", mci: "+0.11 MCI", cost: "Cost-neutral" },
  ];
  return (
    <Box>
      <ExecSummary>
        {t("The AI material engine surfaced {{count}} viable circular substitutes with a combined potential of ~+0.39 weighted MCI at neutral-to-modest cost impact.", { count: matches.length })}
      </ExecSummary>
      <DataTable
        getKey={(m) => m.current}
        columns={[
          { key: "current", label: "Current material" },
          { key: "suggestion", label: "Suggested alternative" },
          { key: "benefit", label: "Environmental benefit", render: (m) => <Box component="span" sx={{ color: GREEN }}>{m.benefit}</Box> },
          { key: "mci", label: "MCI impact" },
          { key: "cost", label: "Cost implication" },
        ]}
        rows={matches}
      />
    </Box>
  );
}

function ImprovementsSection() {
  const { t, tok } = useReportTok();
  const high = recommendations.filter((r) => r.priority === "High");
  const med = recommendations.filter((r) => r.priority === "Medium");
  const low = recommendations.filter((r) => r.priority === "Low");
  const groups = [
    { title: "High priority", items: high, bg: "rgba(101,157,51,0.15)", color: GREEN },
    { title: "Medium priority", items: med, bg: "#fef3c7", color: "#92400e" },
    { title: "Low priority", items: low, bg: tok.bg.card, color: tok.text.muted },
  ];
  return (
    <Box>
      <ExecSummary>
        {t("A prioritised roadmap of {{count}} interventions is projected to raise the Project MCI from {{score}}% toward the mid-50s while reducing embodied carbon by an estimated 12–18%.", {
          count: recommendations.length, score: projectSummary.projectMci,
        })}
      </ExecSummary>
      {groups.map((g) => (
        <Box key={g.title} sx={{ mt: 2 }}>
          <SubHeading>{t(g.title)}</SubHeading>
          <Box sx={{ display: "grid", gap: 1 }}>
            {g.items.map((r) => (
              <Box key={r.title} sx={{ borderRadius: "8px", border: `1px solid ${tok.border.card}`, backgroundColor: tok.bg.card, p: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 500, color: tok.text.primary, fontFamily: tok.font.family }}>{r.title}</Typography>
                  <Box component="span" sx={{ borderRadius: "999px", px: 1, py: 0.25, fontSize: "11px", fontWeight: 600, backgroundColor: g.bg, color: g.color }}>{r.impact}</Box>
                </Box>
                <Typography sx={{ fontSize: "11px", color: tok.text.muted, fontFamily: tok.font.family }}>{t("Effort")}: {r.effort}</Typography>
                <Typography sx={{ mt: 0.5, fontSize: tok.font.size.bodyText, color: tok.text.muted, fontFamily: tok.font.family }}>{r.detail}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ─────────── Full printable report ─────────── */

function FullReport() {
  const { t, isDark } = useReportTok();
  const d = useDerived();
  const today = new Date().toLocaleDateString("en-CA");
  const mats = getLiveMaterials();
  const totalMass = mats.reduce((s, m) => s + (m.mass || 0), 0);
  const virginMass = mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_virgin_content || 0) / 100), 0);
  const recycledInflow = mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_recycled_content || 0) / 100), 0);
  const recycledOut = mats.reduce((s, m) => s + (m.mass || 0) * ((m.eol_recycling || m.om_recycling || 0) / 100), 0);
  const lossOut = totalMass - recycledOut > 0 ? totalMass - recycledOut : 0;
  const pciData = computePciData(liveCombinedData, getTopic(), getProductId());
  const pciScore = (pciData?.percentageOverallPci || 0).toFixed(2);
  
  const inflowOutflowBarData = [
    { name: "Virgin inflow", value: +virginMass.toFixed(1), fill: "#e5766a" },
    { name: "Recycled inflow", value: +recycledInflow.toFixed(1), fill: "#7ed07e" },
    { name: "Recovered output", value: +recycledOut.toFixed(1), fill: "#7fb5e6" },
    { name: "Loss / waste", value: +lossOut.toFixed(1), fill: "#c58bce" },
  ];

  const utilityBarData = [
    { name: "Recycled/Recovered", value: +recycledOut.toFixed(1), fill: "#7ed07e" },
    { name: "Waste/Loss", value: +lossOut.toFixed(1), fill: "#c58bce" },
  ];

  return (
    <Box id="full-report" sx={{ mx: "auto", maxWidth: "210mm", backgroundColor: "#ffffff", p: 0, fontSize: "12px", lineHeight: 1.6, color: "#171717" }}>
      {/* Cover Page */}
      <Box className="report-page" sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "297mm", border: "1px solid #eee", boxSizing: "border-box", p: "25mm !important" }}>
        <Box sx={{ mt: 10 }}>
          <Typography sx={{ fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.25em", color: "#659d33", fontWeight: 600 }}>{t("Circularity Report")}</Typography>
          <Typography sx={{ mt: 2, fontSize: "38px", fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>{getProductName()}</Typography>
          <Box sx={{ mt: 4, height: "4px", width: "80px", backgroundColor: "#659d33", borderRadius: "2px" }} />
        </Box>

        <Box sx={{ display: "grid", gap: 2, borderTop: "1px solid #e5e7eb", pt: 4, mt: "auto" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 1 }}>
            <Typography sx={{ color: "#6b7280", fontWeight: 500 }}>{t("Framework")}:</Typography>
            <Typography sx={{ color: "#111827", fontWeight: 600 }}>ISO 59040:2025 aligned</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 1 }}>
            <Typography sx={{ color: "#6b7280", fontWeight: 500 }}>{t("Date Generated")}:</Typography>
            <Typography sx={{ color: "#111827", fontWeight: 600 }}>{today}</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 1 }}>
            <Typography sx={{ color: "#6b7280", fontWeight: 500 }}>{t("Prepared By")}:</Typography>
            <Typography sx={{ color: "#111827", fontWeight: 600 }}>ARIVU CIRCULARITY ENGINE</Typography>
          </Box>
          <Box sx={{ mt: 2, fontSize: "11px", color: "#9ca3af" }}>
            {t("Confidential – prepared for management review and external sustainability disclosure.")}
          </Box>
        </Box>
      </Box>

      {/* Table of Contents Page */}
      <PrintPage title={t("Table of Contents")}>
        <Box sx={{ mt: 4, display: "grid", gap: 2.5 }}>
          {["Summary", ...sectionList.map((s) => s.label), "Overall Circularity Performance Summary", "Appendix"].map((tt, index) => (
            <Box key={tt} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px dotted #d1d5db", pb: 0.5 }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#374151" }}>
                {index === 0 ? "—" : index === 8 ? "—" : index === 9 ? "—" : `${index}.`} {t(tt)}
              </Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#659d33" }}>
                {index + 1}
              </Typography>
            </Box>
          ))}
        </Box>
      </PrintPage>

      {/* Summary Page */}
      <PrintPage title={t("Summary")} pageNo={1}>
        <Box sx={{ mt: 2 }}>
          <Box component="p" sx={{ m: 0, fontSize: "15px", lineHeight: 1.7, color: "#374151", textAlign: "left" }}>
            {t("{{name}} achieves a Material Circularity Indicator score of {{score}}% and {{mass}} kg of total mass, with a cradle-to-gate embodied carbon of {{gwp}} t CO₂e. The product outperforms the industry average and is positioned as an emerging circularity leader.", {
              name: getProductName(), score: projectSummary.projectMci, mass: projectSummary.totalMass.toLocaleString(), gwp: projectSummary.totalGwp.toLocaleString(),
            })}
          </Box>
          <Box component="p" sx={{ mt: 3, fontSize: "14px", lineHeight: 1.7, color: "#4b5563", textAlign: "left" }}>
            {t("Key strengths are long service life, high steel and secondary-aluminium content, and a renewable-based insulation core. Primary opportunities relate to virgin aluminium substitution, formal recovery of coated components and upgradeability documentation.")}
          </Box>
        </Box>
      </PrintPage>

      {/* 1. Product Information */}
      <PrintPage title={`1. ${t("Product Information")}`} pageNo={2}>
        <ProductInfoSection />
      </PrintPage>

      {/* 2. Material Composition */}
      <PrintPage title={`2. ${t("Material Composition")}`} pageNo={3}>
        <CompositionSection />
      </PrintPage>

      {/* 3. Circularity Inputs & Outputs - Data Tables */}
      <PrintPage title={`3. ${t("Circularity Inputs & Outputs")}`} pageNo={4}>
        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography sx={{ fontSize: "13px", color: "#4b5563", mb: 2, textAlign: "left" }}>
            {t("This section presents the physical flows consolidated from the circularity database records. Material inflows map the raw feedstock inputs (virgin vs. recycled ratio), and outflows track expected recovery pathways (recycling efficiency or end-of-life collection potential).")}
          </Typography>
          <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "1fr 1fr" }}>
            <Box>
              <SubHeading>{t("Inflows")}</SubHeading>
              <KVTable rows={[
                ["Primary (virgin)", `${virginMass.toFixed(1)} kg`],
                ["Secondary (recycled)", `${recycledInflow.toFixed(1)} kg`],
                ["Total inflow", `${totalMass.toFixed(1)} kg`],
              ]} />
            </Box>
            <Box>
              <SubHeading>{t("Outflows")}</SubHeading>
              <KVTable rows={[
                ["Recovered output", `${recycledOut.toFixed(1)} kg`],
                ["Waste / loss", `${(totalMass - recycledOut).toFixed(1)} kg`],
                ["Recovery rate", totalMass > 0 ? `${(recycledOut / totalMass * 100).toFixed(1)}%` : "—"],
              ]} />
            </Box>
          </Box>
        </Box>
      </PrintPage>

      {/* 3. Circularity Inputs & Outputs - Horizonal Chart */}
      <PrintPage title={`3. ${t("Circularity Inputs & Outputs — Inflow vs. Outflow")}`} pageNo={5}>
        <Box sx={{ mt: 4, height: 420, width: "100%", "& .recharts-responsive-container": { width: "100% !important", minWidth: "500px" } }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={inflowOutflowBarData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="name" fontSize={11} tick={{ fill: "#374151" }} />
              <YAxis fontSize={11} tick={{ fill: "#6b7280" }} unit=" kg" />
              <Tooltip formatter={(v) => [`${v} kg`]} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {inflowOutflowBarData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </PrintPage>

      {/* 4. Utility - Data Tables */}
      <PrintPage title={`4. ${t("Utility")}`} pageNo={6}>
        <KVTable rows={[
          ["Reference lifetime", `${getServiceLife()} years`],
          ["Actual lifetime", `${getServiceLife()} years`],
          ["Maintenance frequency", "Annual visual inspection"],
          ["Repairability", "High"],
          ["Upgradeability", "Medium"],
          ["Reusability", "High"],
        ]} />
        <SubHeading>{t("Per-material utility")}</SubHeading>
        <DataTable
          getKey={(r) => r.name}
          columns={[
            { key: "name", label: "Material" },
            { key: "life", label: "Life (yrs)", align: "right", render: (r) => (typeof r.life === "number" ? r.life.toFixed(2) : r.life) },
            { key: "ind", label: "Reference", align: "right", render: (r) => (typeof r.ind === "number" ? r.ind.toFixed(2) : r.ind) },
            { key: "uf", label: "Utility factor", align: "right", render: (r) => r.uf.toFixed(2) },
          ]}
          rows={mats.map((m) => {
            const lifeVal = m.uf_product_life !== undefined && m.uf_product_life !== null && !isNaN(Number(m.uf_product_life))
              ? Number(m.uf_product_life)
              : getServiceLife();
            const indVal = m.uf_industry_avg_life !== undefined && m.uf_industry_avg_life !== null && !isNaN(Number(m.uf_industry_avg_life))
              ? Number(m.uf_industry_avg_life)
              : getServiceLife();
            const ufVal = m.uf_calculations !== undefined && m.uf_calculations !== null && !isNaN(Number(m.uf_calculations))
              ? Number(m.uf_calculations)
              : (indVal > 0 ? lifeVal / indVal : 1.0);
            return {
              name: m.description,
              life: lifeVal,
              ind: indVal,
              uf: ufVal,
            };
          })}
        />
      </PrintPage>

      {/* 4. Utility - Outflow vertical column chart */}
      <PrintPage title={`4. ${t("Utility — End-of-Life Outflow Distribution")}`} pageNo={7}>
        <Box sx={{ mt: 4, height: 420, width: "100%", "& .recharts-responsive-container": { width: "100% !important", minWidth: "500px" } }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={utilityBarData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="name" fontSize={11} tick={{ fill: "#374151" }} />
              <YAxis fontSize={11} tick={{ fill: "#6b7280" }} unit=" kg" />
              <Tooltip formatter={(v) => [`${v} kg`]} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {utilityBarData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </PrintPage>

      {/* 5. Material Circularity Indicator - Data Table */}
      <PrintPage title={`5. ${t("Material Circularity Indicator")}`} pageNo={8}>
        <DataTable
          getKey={(m) => m.id || m.description}
          columns={[
            { key: "description", label: "Material" },
            { key: "im_virgin_content", label: "Virgin %", align: "right", render: (m) => `${(m.im_virgin_content || 0).toFixed(0)}%` },
            { key: "im_recycled_content", label: "Recycled %", align: "right", render: (m) => `${(m.im_recycled_content || 0).toFixed(0)}%` },
            { key: "eol_recycling_efficiency", label: "Recycling eff.", align: "right", render: (m) => `${(m.eol_recycling_efficiency || 0).toFixed(0)}%` },
            { key: "row_mci", label: "MCI", align: "right", render: (m) => ((m.row_mci || 0) * 100).toFixed(1) + "%" },
          ]}
          rows={mats}
        />
      </PrintPage>

      {/* 5. Material Circularity Indicator - Charts & Component Wise MCI */}
      <PrintPage title={`5. ${t("Material Circularity Indicator — Summary & Component Indicators")}`} pageNo={9}>
        <Box sx={{ display: "grid", gap: 3 }}>
          <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: "200px 1fr" }}>
            <Box sx={{ borderRadius: "10px", border: "1px solid #e5e7eb", backgroundColor: "rgba(101,157,51,0.08)", p: 3, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>{t("Material Circularity Indicator")}</Typography>
              <Typography sx={{ mt: 1, fontSize: "40px", fontWeight: 700, color: "#111827" }}>{getLiveMciPct().toFixed(1)}%</Typography>
              <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{t("Weighted, mass-based")}</Typography>
            </Box>
            <Box sx={{ height: 200, width: "100%", "& .recharts-responsive-container": { width: "100% !important", minWidth: "300px" } }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={benchmarks} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" domain={[0, 100]} fontSize={11} />
                  <YAxis type="category" dataKey="label" fontSize={11} width={110} />
                  <Tooltip />
                  <Bar dataKey="mci" fill="#7dbf3f" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <Box sx={{ borderTop: "1px solid #e5e7eb", pt: 3 }}>
            <SubHeading>{t("Component-wise Material Circularity Indicator")}</SubHeading>
            <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
              {mats.map((m) => (
                <Box key={m.id || m.description} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography sx={{ width: 220, flexShrink: 0, fontSize: "13px", fontWeight: 500, color: "#171717", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" }}>
                    {m.description}
                  </Typography>
                  <Box sx={{ height: 12, flex: 1, borderRadius: "6px", backgroundColor: "#f3f4f6", overflow: "hidden" }}>
                    <Box sx={{ height: "100%", borderRadius: "6px", backgroundColor: GREEN, width: `${((m.row_mci || 0) * 100).toFixed(0)}%` }} />
                  </Box>
                  <Typography sx={{ width: 56, textAlign: "right", fontSize: "13px", fontWeight: 600, color: "#171717" }}>
                    {((m.row_mci || 0) * 100).toFixed(1)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </PrintPage>

      {/* 6. Design Assessment (PCI) */}
      <PrintPage title={`6. ${t("Design Assessment (PCI)")}`} pageNo={10}>
        <DesignAssessmentSection />
      </PrintPage>

      {/* 7. Analytics - Feedstock Charts & KPI Cards */}
      <PrintPage title={`7. ${t("Analytics")}`} pageNo={11}>
        <Box sx={{ display: "grid", gap: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
            <MetricCard title={t("Material Circularity Indicator")} value={getLiveMciPct().toFixed(2)} unit="%" icon={LuGauge} accent="#16a34a" bg="rgba(22, 163, 74, 0.1)" variant="stage" />
            <MetricCard title={t(renameProductWord("Product Circularity Indicator"))} value={pciScore} unit="%" icon={LuBox} accent="#0d9488" bg="rgba(13, 148, 136, 0.1)" variant="stage" />
            <MetricCard title={t("Embodied carbon")} value={(getLiveGwpSum() || 0).toFixed(2)} unit="kg CO₂e" icon={LuCloud} accent="#2563eb" bg="rgba(37, 99, 235, 0.1)" variant="stage" />
            <MetricCard title={t("Recovery rate")} value={totalMass > 0 ? (recycledOut / totalMass * 100).toFixed(2) : 0} unit="%" icon={LuRecycle} accent="#ea580c" bg="rgba(234, 88, 12, 0.1)" variant="stage" />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mt: 2 }}>
            <Box sx={{ height: 280, width: "100%" }}>
              <CircularChart title="Material Circularity Indicator Contribution" combined_data={liveCombinedData} />
            </Box>
            <Box sx={{ height: 280, width: "100%" }}>
              <InputMaterialChart height={240} circularities={liveCombinedData?.combined_circularities || []} />
            </Box>
          </Box>
        </Box>
      </PrintPage>

      {/* 7. Analytics - GWP vs MCI Scatter Page */}
      <PrintPage title={`7. ${t("Analytics — GWP vs. MCI & Output split")}`} pageNo={12}>
        <Box sx={{ display: "grid", gap: 3 }}>
          <Box sx={{ height: 220, width: "100%" }}>
            <NomarlizedGwpVsPercentageMciChart topic="Product" combined_data={liveCombinedData} />
          </Box>
          <Box sx={{ height: 220, mt: 2, width: "100%" }}>
            <OutputMaterialChart height={200} circularities={liveCombinedData?.combined_circularities || []} />
          </Box>
        </Box>
      </PrintPage>

      {/* Performance Summary */}
      <PrintPage title={t("Overall Circularity Performance Summary")} pageNo={13}>
        <Box sx={{ mt: 2 }}>
          <KpiGrid items={[
            { label: "Material Circularity Indicator", value: `${projectSummary.projectMci}%` },
            { label: "Weighted MCI", value: d.weightedMci.toFixed(2) },
            { label: "Recovery efficiency", value: `${((d.recycledOut + d.reusedOut) / d.totalMass * 100).toFixed(0)}%` },
            { label: "Renewable content", value: `${(d.renewableMass / d.totalMass * 100).toFixed(1)}%` },
          ]} />
          <Box component="p" sx={{ mt: 4, fontSize: "14px", lineHeight: 1.7, color: "#374151", textAlign: "left" }}>
            {t("Overall performance is")} <strong>{t("above industry average and below best-in-class")}</strong>. {t('Implementing the "high priority" recommendations would move the assembly into the leading quartile for its category.')}
          </Box>
        </Box>
      </PrintPage>

      {/* Appendix */}
      <PrintPage title={t("Appendix")} pageNo={14}>
        <SubHeading>{t("Calculation assumptions")}</SubHeading>
        <Box component="ul" sx={{ pl: 2.5, fontSize: "13px", color: "#4b5563", mb: 2 }}>
          <li>{t("Mass balance normalised at product functional unit.")}</li>
          <li>{t("Recycling efficiency uses material-specific factors from Ecoinvent 3.10.")}</li>
          <li>{t("Utility factor computed as (L / L_avg) × (U / U_avg) per EMF MCI methodology.")}</li>
        </Box>
        <SubHeading>{t("Methodology")}</SubHeading>
        <Box component="p" sx={{ fontSize: "13px", color: "#4b5563", mb: 2 }}>{t("Ellen MacArthur Foundation MCI v1.0 methodology; ISO 59020:2024 indicator set; ISO 59040:2025 PCDS payload conventions.")}</Box>
        <SubHeading>{t("Definitions")}</SubHeading>
        <Box component="ul" sx={{ pl: 2.5, fontSize: "13px", color: "#4b5563", mb: 2 }}>
          <li><strong>{t("MCI")}:</strong> {t("Material Circularity Indicator (0–1).")}</li>
          <li><strong>{t("LFI")}:</strong> {t("Linear Flow Index (0–1); lower is more circular.")}</li>
          <li><strong>{t("Utility factor")}:</strong> {t("Relative product usefulness vs industry reference.")}</li>
        </Box>
        <SubHeading>{t("Acronyms")}</SubHeading>
        <Box component="p" sx={{ fontSize: "13px", color: "#4b5563", mb: 2 }}>{t("GWP – Global Warming Potential • PCDS – Product Circularity Data Sheet • EMF – Ellen MacArthur Foundation • EPD – Environmental Product Declaration.")}</Box>
        <SubHeading>{t("Data quality notes")}</SubHeading>
        <Box component="p" sx={{ fontSize: "13px", color: "#4b5563", mb: 2 }}>{t("Primary data used for aluminium and steel inputs; secondary/database data for insulation and coatings. Overall confidence: medium-high.")}</Box>
        <SubHeading>{t("ISO references")}</SubHeading>
        <Box component="p" sx={{ fontSize: "13px", color: "#4b5563" }}>ISO 59020:2024, ISO 59040:2025, ISO 14040/44:2006, EN 15804+A2:2019.</Box>
      </PrintPage>
    </Box>
  );
}

function PrintPage({ title, pageNo, children }) {
  const { t } = useReportTok();
  return (
    <Box component="section" className="report-page" sx={{ display: "flex", flexDirection: "column", height: "297mm", boxSizing: "border-box", border: "1px solid #eee" }}>
      {/* Left aligned header only */}
      <Box component="header" sx={{ mb: 4, display: "flex", alignItems: "center", borderBottom: "1px solid #e5e7eb", pb: 1.5, fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>
        <span>{t("Arivu Circularity Report")} — {getProductName()}</span>
      </Box>
      
      <Typography sx={{ mb: 3, fontSize: "22px", fontWeight: 700, color: "#111827", borderBottom: "2px solid #659d33", pb: 1, display: "inline-block" }}>
        {title}
      </Typography>
      
      <Box sx={{ fontSize: "13px", color: "#374151", flex: 1 }}>
        {children}
      </Box>
      
      {/* Footer with page number only in center */}
      <Box component="footer" sx={{ mt: "auto", display: "flex", justifyContent: "center", borderTop: "1px solid #e5e7eb", pt: 1.5, fontSize: "11px", color: "#6b7280", fontWeight: 600 }}>
        <span>{pageNo ? `${pageNo}` : ""}</span>
      </Box>
    </Box>
  );
}

export default CircularityReportPanel;
